/* global Routes */
import cx from "classnames";
import { ConfirmationDialog } from "common/styleguide/ConfirmationDialog";
import { actions as plannerReducerActions } from "features/planner/Planner/reducer.ts";
import { useFormatMessage } from "lib/localization";
import _ from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { defineMessages, FormattedMessage as T } from "react-intl";
import { ClickActions } from "../ClickActions";
import { icons } from "../icons";
import { LocationSearch } from "../LocationSearch";
import { RouteTools } from "../RouteTools";
import { SelectionToolHelper } from "../SelectionToolHelper";
import { SelectionTools } from "../SelectionTools";
import { TraceTool } from "../TraceTool";
import { useForceUpdate } from "../useForceUpdate";
import styles from "./RightSidebar.scss";

export const messages = defineMessages({
  clearMap: {
    id: "RightSidebar.clearMapConfirm",
    defaultMessage: "Are you sure you want to clear the map?",
  },
  reverseRoute: {
    id: "RightSidebar.reverseRouteConfirm",
    defaultMessage:
      "Are you sure? This will rebuild your route from scratch, and you will lose any custom cues you may have entered.",
  },
  clearCues: {
    id: "RightSidebar.clearCuesConfirm",
    defaultMessage: "Are you sure? This will delete your entire cuesheet!",
  },
  clearPoi: {
    id: "RightSidebar.clearPoiConfirm",
    defaultMessage: "Are you sure? This will delete all POI!",
  },
  undo: {
    id: "RightSidebar.undo",
    defaultMessage: "Undo",
  },
  redo: {
    id: "RightSidebar.redo",
    defaultMessage: "Redo",
  },
});

const selectionActions = {
  deleteSelection: () => Routes.sampleGraph.controlsBar.removePoints(),
  flattenElevation: () => Routes.sampleGraph.controlsBar.flattenElevation(),
  changeColor: ({ color }) => Routes.sampleGraph.controlsBar.changeColorTo(parseInt("0x" + color.split("#")[1])),
  removeControlPoints: () => Routes.sampleGraph.controlsBar.removeControlPoints(),
  excludeFromMetrics: () => Routes.sampleGraph.controlsBar.excludeFromMetrics(),
  crop: () => Routes.sampleGraph.controlsBar.crop(),
  changeSurface: ({ surface }) => Routes.sampleGraph.controlsBar.changeSurfaceTo(parseInt(surface, 10)), // parse OrderedMap string key
};

const _RightSidebar = ({ plannerState, plannerDispatch, actions, ...props }) => {
  const needsUpgrade = !window.rwgps.config.privileges.advanced_planning;
  const fm = useFormatMessage();

  const clickActionProps = _.pick(props, [
    "optimizeForDefault",
    "optimizeForCallback",
    "avoidHighwaysChanged",
    "setCallbacks",
    "unsetCallbacks",
  ]);

  // Show selection tools when a selection is made
  const [showSelectionTools, setShowSelectionTools] = useState(false);
  const initSG = () => {
    if (!Routes.sampleGraph) {
      setTimeout(initSG, 500);
    }

    const onSelect = (slice) => {
      plannerDispatch(plannerReducerActions.setSelectedFeature(slice));
      setShowSelectionTools(!!slice);
    };
    const events = ["dataslice:select", "segmentselector:unload"];
    // sample graph isn't initialized right away so we poll for it
    const interval = setInterval(() => {
      if (Routes.sampleGraph) {
        events.forEach((ev) => Routes.sampleGraph.bindTo(Routes.sampleGraph, ev, onSelect));
        clearInterval(interval);
      }
    }, 100);
    return () => events.forEach((ev) => Routes.sampleGraph.unbindFrom(Routes.sampleGraph, ev, onSelect));
  };
  useEffect(initSG, []);

  // reducer selectedFeature state can change based on actions elsewhere in the planner.
  // keep local showSelectionTools state in sync with reducer selectedFeature state, and unload
  // the sg controlsBar in the case that reducer selectedFeature state is cleared.
  useEffect(() => {
    if (showSelectionTools && !plannerState.selectedFeature && Routes.sampleGraph?.controlsBar) {
      setShowSelectionTools(false);
      Routes.sampleGraph.controlsBar.unload();
    }
  }, [plannerState.selectedFeature]);

  // Clear the selectedFeature once an action is performed
  useEffect(() => {
    if (!showSelectionTools && plannerState.selectedFeature && Routes.sampleGraph?.controlsBar) {
      Routes.sampleGraph.controlsBar.unload();
    }
  }, [showSelectionTools]);

  // When selection tools are used without an initial selection, we have a SelectionToolHelper for that
  const [selectionToolHelperAction, setSelectionToolHelperAction] = useState();
  const performSelectionAction = (action) => {
    if (!["flattenElevation", "changeSurface"].includes(action) && needsUpgrade) {
      actions.promptUpgrade(action);
      return;
    }
    if (plannerState.selectedFeature && ["changeColor", "changeSurface"].indexOf(action) === -1) {
      selectionActions[action]();
    } else {
      setSelectionToolHelperAction(action);
    }
  };

  // Listen for route changes so we can show the route tools as disabled when the active route is empty
  const forceUpdate = useForceUpdate();
  const toolsDisabled = !Routes.activeMap?.activeRoute.metrics.distance;
  useEffect(() => {
    window.rwgps.Events.bind("route:modified", forceUpdate);
    window.rwgps.Events.bind("map:routes_changed", forceUpdate);
    return () => {
      window.rwgps.Events.unbind("route:modified", forceUpdate);
      window.rwgps.Events.unbind("map:routes_changed", forceUpdate);
    };
  }, []);

  // Need to actively manage sidebar height in order to get vertical scrolling when there is overflow
  const sidebarRef = useRef();
  const onResize = _.throttle(
    () => {
      sidebarRef.current.style.height = window.innerHeight - (sidebarRef.current.getClientRects()[0] || {}).top + "px";
    },
    50,
    { leading: true, trailing: true }
  );
  useEffect(() => {
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const [modalConfirmation, setModalConfirmation] = useState();

  return (
    <>
      <div
        ref={sidebarRef}
        className={cx(
          styles.PlannerSidebar,
          (selectionToolHelperAction || plannerState.showTraceTool) && styles.fullBleed
        )}
      >
        {selectionToolHelperAction && (
          <SelectionToolHelper
            action={selectionToolHelperAction}
            performAction={selectionActions[selectionToolHelperAction]}
            close={() => setSelectionToolHelperAction(null)}
          />
        )}
        {plannerState.showTraceTool && (
          <TraceTool
            onRequestClose={() => plannerDispatch(plannerReducerActions.setShowTraceTool(false))}
            manualTrace={actions.manualTrace}
          />
        )}

        {!selectionToolHelperAction && !plannerState.showTraceTool && (
          <>
            <div className={styles.topArea}>
              <div className={cx(styles.boxes)}>
                <div onClick={() => actions.undo()}>
                  {icons.undo}
                  {fm(messages.undo)}
                </div>
                <div onClick={() => actions.redo()}>
                  {icons.redo}
                  {fm(messages.redo)}
                </div>
                <div onClick={() => setModalConfirmation("clearMap")}>
                  {icons.clearMap}
                  <T id="RightSidebar.clearMap" defaultMessage="Clear Map" />
                </div>
              </div>
              <LocationSearch />
            </div>
            <hr />
            <ClickActions
              {...clickActionProps}
              command={plannerState.clickActionCommand}
              dispatch={plannerDispatch}
              needsUpgrade={needsUpgrade}
            />
            <hr />
            {!showSelectionTools && (
              <RouteTools
                {...actions}
                needsUpgrade={needsUpgrade}
                toolsDisabled={toolsDisabled}
                setModalConfirmation={setModalConfirmation}
                showSelectionTools={() => setShowSelectionTools(true)}
              />
            )}
            {showSelectionTools && (
              <SelectionTools
                needsUpgrade={needsUpgrade}
                selection={plannerState.selectedFeature}
                performAction={performSelectionAction}
                closeSelectionTools={() => setShowSelectionTools(false)}
              />
            )}
          </>
        )}

        {modalConfirmation && (
          <ConfirmationDialog
            onRequestClose={() => setModalConfirmation(null)}
            onCancel={() => setModalConfirmation(null)}
            onConfirm={() => {
              actions[modalConfirmation]();
              setModalConfirmation(null);
            }}
          >
            {fm(messages[modalConfirmation])}
          </ConfirmationDialog>
        )}
      </div>
    </>
  );
};

export const RightSidebar = (props) => {
  if (!props.userPrefs) {
    // delay rendering the right sidebar contents until currentUser is
    // available from redux. otherwise, user default preferences are not respected
    // see https://github.com/ridewithgps/ridewithgps/issues/10534
    return <div style={{ height: "100%", width: "255px" }} />;
  }
  return <_RightSidebar {...props} />;
};
